import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Logo, type LogoVariant } from '../logo/logo.component';
import styles from './app-shell.component.module.css';
import { ThemeToggle } from './theme-toggle.component';

interface AppShellProps {
	/** Name of the current app; shown as a text label when `nav` is not given. */
	appName: string;
	/** Which brand mark the header shows. Defaults to the per-app `tasks` wordmark. */
	brand?: LogoVariant;
	/** Optional navigation slot (e.g. section links). Falls back to `appName`. */
	nav?: ReactNode;
	/** Optional account slot (e.g. an avatar). Falls back to a placeholder button. */
	user?: ReactNode;
	/** Full-bleed content — drop the max-width cap (e.g. the board). */
	fluid?: boolean;
	children: ReactNode;
}

/**
 * Reusable base shell: header with logo, a nav slot, theme toggle and an
 * account slot, plus a content slot. Apps pass real nav/user; without them it
 * degrades to the app name and a placeholder account button.
 */
export function AppShell({ appName, brand, nav, user, fluid, children }: AppShellProps) {
	const { t } = useTranslation();
	return (
		<div className={styles.shell}>
			<header>
				<a href="/" aria-label="Home">
					<Logo variant={brand} />
				</a>
				<nav aria-label={t('nav.sections', 'Sections')}>{nav ?? appName}</nav>
				<div>
					<ThemeToggle />
					{user ?? (
						<button
							type="button"
							className={styles.userMenu}
							aria-label={t('nav.account', 'Account')}
						>
							<span aria-hidden>◦</span>
						</button>
					)}
				</div>
			</header>
			<main className={fluid ? styles.fluid : undefined}>{children}</main>
		</div>
	);
}
